import * as next from '../../../types/next'
import type {
  ContractBasedProductRecommendationsBlock,
  EntityBasedProductRecommendationsBlock
} from '../../../types/next'
import { CONTROL_NAME } from '../../../utils'
import type * as current from '../../../utils/types/sharedTypes'
import type { UpBlockHandler } from '../types'
import { findBlockIdWithStepId, newId } from '../utils'

import { upCommonSettings } from './common'

const upReferences = (
  uischema: current.BlockUiSchema,
  journey?: current.Journey
) => {
  const references: Record<next.ReferenceId, next.VariablePriceReference> = {}

  if (!journey) {
    return references
  }

  const blockMappings = uischema.options?.blockMappings ?? []

  blockMappings.forEach((mapping: any) => {
    if (mapping.type === 'pricing' || mapping.type === 'indirect-pricing') {
      const blockId = findBlockIdWithStepId(
        journey,
        mapping.stepId,
        mapping.blockName
      )
      const referenceId = newId<next.ReferenceId>()

      references[referenceId] = {
        meaning: 'variable-price',
        referenceType: next.REFERENCED_TYPE.BLOCK,
        referenceId: blockId,
        id: referenceId,
        metadata: {
          unit: mapping.metadata.unit,
          recurrence: mapping.metadata.recurrence,
          productId: mapping.metadata.productId,
          price: {
            priceId: mapping.metadata.priceId,
            componentId: mapping.metadata.priceComponentId
          }
        }
      } as next.VariablePriceReference
    } else {
      throw new Error('BlockMapping type not recognized', mapping.type)
    }
  })

  return references
}

export const up: UpBlockHandler<next.ProductRecommendationsBlock> = (
  uischema,
  _,
  journey
) => {
  if (uischema.type !== CONTROL_NAME.PRODUCT_RECOMMENDATIONS_CONTROL) {
    throw new Error('Expected a valid Product recommendations control')
  }

  const upRecommendationSource = (source: string) => {
    if (!source) {
      // Use contract as the default source when no source is selected
      return next.ProductRecommendationSource.CONTRACT
    }

    if (source === 'block_config')
      return next.ProductRecommendationSource.RECOMMENDATIONS

    if (source === 'context_data')
      return next.ProductRecommendationSource.CONTRACT

    throw new Error(`Invalid product recommendation source ${source}`)
  }

  const references = upReferences(uischema, journey) as Record<
    next.ReferenceId,
    next.ContractReference | next.VariablePriceReference
  >

  const source = upRecommendationSource(uischema.options?.source)

  switch (source) {
    case next.ProductRecommendationSource.CONTRACT: {
      const referenceId = newId<next.ReferenceId>()

      references[referenceId] = {
        meaning: 'source',
        id: referenceId,
        referenceType: next.REFERENCED_TYPE.CONTEXT_PARAMETER,
        referenceId: uischema.options?.contextParameter
      }

      return {
        type: next.BLOCK_TYPE.PRODUCT_RECOMMENDATIONS,
        settings: {
          ...upCommonSettings(uischema),
          source: next.ProductRecommendationSource.CONTRACT
        },
        references: references
      } as ContractBasedProductRecommendationsBlock
    }
    case next.ProductRecommendationSource.RECOMMENDATIONS:
      return {
        type: next.BLOCK_TYPE.PRODUCT_RECOMMENDATIONS,
        settings: {
          ...upCommonSettings(uischema),
          source: next.ProductRecommendationSource.RECOMMENDATIONS,
          recommendationIds: uischema.options?.productRecommendations
        },
        references: references
      } as EntityBasedProductRecommendationsBlock
  }
}
